import React from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, Wrench } from "lucide-react";

const money = (v) => `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const per = (c) => (c === "annual" ? "an" : "mois");

/** Bandeau « nouveau montant de prélèvement » (révision PayPal à approuver ou ajustement manuel PAG). */
export function RevisionBanner({ revision, onRefresh }) {
  if (!revision) return null;
  const pending = revision.status === "pending_approval";
  const check = async () => {
    try {
      const { data } = await api.post("/chef/facturation/revision/check");
      if (data.revision?.status === "approved") toast.success("Nouveau montant confirmé par PayPal");
      else toast.info(data.revision?.check_error ? "Vérification impossible pour l'instant" : "PayPal n'a pas encore confirmé — réessayez après approbation");
      onRefresh?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className={`mb-6 border-2 p-5 flex flex-wrap items-start gap-4 ${pending ? "border-[#002FA7] bg-[#F5F8FF]" : "border-[#FFD700] bg-[#FFFBEB]"}`} data-testid="revision-banner" data-status={revision.status}>
      {pending ? <ExternalLink className="h-5 w-5 text-[#002FA7] mt-0.5" /> : <Wrench className="h-5 w-5 text-[#B45309] mt-0.5" />}
      <div className="flex-1 min-w-[240px]">
        <div className="font-display font-black text-base" data-testid="revision-amount">Nouveau montant de prélèvement : {money(revision.amount)}/{per(revision.cycle)}{revision.previous_amount ? <span className="text-[#868E96] font-semibold text-sm"> (au lieu de {money(revision.previous_amount)})</span> : null}</div>
        <div className="text-xs text-[#495057] mt-1">{revision.reason}. {pending ? "PayPal exige votre accord : approuvez le nouveau montant, il s'appliquera dès la prochaine échéance." : "L'équipe PAG ajuste votre prélèvement manuellement — aucune action de votre part."}</div>
        {revision.lines?.length > 0 && <div className="text-[11px] text-[#495057] mt-2" data-testid="revision-lines">{revision.lines.map((l) => `${l.label} ${money(l.total_ht)}`).join(" · ")}</div>}
      </div>
      {pending && (
        <div className="flex flex-col gap-2">
          <a href={revision.approve_url} target="_blank" rel="noreferrer" data-testid="revision-approve" className="inline-flex items-center h-10 px-4 bg-[#0070BA] hover:bg-[#005a94] text-white font-semibold text-sm">Approuver le nouveau montant sur PayPal →</a>
          <Button variant="outline" size="sm" onClick={check} data-testid="revision-check" className="h-8 text-xs"><RefreshCw className="h-3.5 w-3.5 mr-1" />J'ai approuvé — vérifier</Button>
        </div>
      )}
    </div>
  );
}
